import {
  ANT,
  ANTState,
  ARIORead,
  ArNSNameDataWithName,
  mARIOToken,
} from '@ar.io/sdk/web';
import { ArweaveCompositeDataProvider } from '@src/services/arweave/ArweaveCompositeDataProvider';
import { AoAddress } from '@src/types';
import { QueryClient, useQuery } from '@tanstack/react-query';
import {
  PersistedClient,
  Persister,
} from '@tanstack/react-query-persist-client';
import { del, get, set } from 'idb-keyval';

import { isArweaveTransactionID } from '.';

/**
 * Creates an Indexed DB persister
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API
 */
export function createIDBPersister(idbValidKey: IDBValidKey = 'reactQuery') {
  return {
    persistClient: async (client: PersistedClient) => {
      await set(idbValidKey, client);
    },
    restoreClient: async () => {
      return await get<PersistedClient>(idbValidKey);
    },
    removeClient: async () => {
      await del(idbValidKey);
    },
  } as Persister;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 1 day
      staleTime: 1000 * 60 * 5, // 5 minutes — prevent aggressive refetches
      refetchOnWindowFocus: false, // stop tab-focus refetch storms
    },
  },
});

export function buildAntStateQuery({
  processId,
  hyperbeamUrl,
  solana,
}: {
  processId: string;
  hyperbeamUrl?: string;
  /** When true, init the SDK against the Solana ANT program instead of AO. */
  solana?: boolean;
}): Parameters<typeof useQuery<ANTState>>[0] {
  return {
    queryKey: ['ant', processId, solana ? 'solana' : 'ao'],
    queryFn: async () => {
      if (!processId) throw new Error('Must provide a token address');
      // AO ids are 43-char Arweave txids; Solana ids are 32–44-char base58
      // Metaplex Core asset addresses. Only enforce the Arweave shape on AO.
      if (!solana && !isArweaveTransactionID(processId)) {
        throw new Error('Must provide a valid token address');
      }

      // Lazy-import sdk-init to avoid a circular import via constants.
      const { buildAntRead } = await import('./sdk-init');
      const ant = await buildAntRead({ solana, processId, hyperbeamUrl });
      return await ant.getState();
    },
    staleTime: Infinity,
  };
}

export function buildIOBalanceQuery({
  arioContract,
  address,
  meta,
}: {
  arioContract: ARIORead;
  address: string;
  meta?: string[];
}): Parameters<typeof useQuery<number>>[0] {
  return {
    queryKey: ['io-balance', address, ...(meta || [])],
    queryFn: async () => {
      return await arioContract
        .getBalance({
          address,
        })
        .then((balance) => {
          return new mARIOToken(balance).toARIO().valueOf();
        })
        .catch(() => 0);
    },
    staleTime: 1000 * 60 * 60, // one hour
  };
}
export function buildARBalanceQuery({
  provider,
  address,
  meta,
}: {
  provider: ArweaveCompositeDataProvider;
  address: AoAddress;
  meta?: string[];
}): {
  queryKey: ['ar-balance', string] | string[];
  queryFn: () => Promise<number>;
  staleTime: number;
} {
  return {
    queryKey: ['ar-balance', address.toString(), ...(meta || [])],
    queryFn: async () => {
      return await provider.getArBalance(address).catch(() => 0);
    },
    staleTime: 1000 * 60 * 60, // one hour
  };
}

export function buildArNSRecordsQuery({
  arioContract,
  filters,
}: {
  arioContract: ARIORead;
  filters?: Partial<
    Record<
      keyof ArNSNameDataWithName,
      string | number | boolean | string[] | number[] | boolean[]
    >
  >;
}): Parameters<typeof useQuery<ArNSNameDataWithName[]>>[0] {
  // Solana `ARIO` instances don't expose a `.process.processId` (no AO process
  // backs them) — fall back to a stable sentinel for the cache key in that
  // case so React Query can still de-dup the query.
  const cacheKey =
    (arioContract as { process?: { processId?: string } }).process?.processId ??
    'solana';
  return {
    queryKey: ['arns-records', cacheKey, filters],
    queryFn: async () => {
      let hasMore = true;
      let cursor = undefined;
      const records = [];
      while (hasMore) {
        const {
          items,
          hasMore: more,
          nextCursor,
        } = await arioContract.getArNSRecords({
          filters,
          cursor,
          limit: 1000,
        });
        hasMore = more;
        cursor = nextCursor;
        records.push(...items);
      }
      return records;
    },
    staleTime: 4 * 60 * 60 * 1000, // 1 hour
  };
}
